import * as React from "react";
import { ChartsContainer } from "@mui/x-charts/ChartsContainer";
import { LinePlot, MarkPlot } from "@mui/x-charts/LineChart";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip";
import { ChartsAxisHighlight } from "@mui/x-charts/ChartsAxisHighlight"; // adds line where mouse is
import { ChartsGrid } from "@mui/x-charts/ChartsGrid";
import { LineHighlightPlot } from "@mui/x-charts/LineChart"; // adds dot where mouse is
import { ChartsReferenceLine } from "@mui/x-charts/ChartsReferenceLine";

import { UncertaintyArea, DayLines } from "./plotUtil";
import { round } from "./util";

// IDEAS: add continuous color? https://mui.com/x/react-charts/legend/
// Favicon: https://www.vexels.com/png-svg/preview/285289/wing-foil-people-silhouette
// ICO converter https://www.icoconverter.com/

function ConvertWind(array) {
  let conversion = 1.9438444924406;
  let q25 = array.data[0].map(function (x) {
    return x * conversion;
  });
  let q50 = array.data[1].map(function (x) {
    return x * conversion;
  });
  let q75 = array.data[2].map(function (x) {
    return x * conversion;
  });
  return [q25, q50, q75];
}

function TranslateDir(x) {
  if (x >= 348.75 || x < 11.25) {
    return "N";
  } else if (x >= 11.25 && x < 33.75) {
    return "NNO";
  } else if (x >= 33.75 && x < 56.25) {
    return "NO";
  } else if (x >= 56.25 && x < 78.75) {
    return "ONO";
  } else if (x >= 78.75 && x < 101.25) {
    return "O";
  } else if (x >= 101.25 && x < 123.75) {
    return "OSO";
  } else if (x >= 123.75 && x < 146.25) {
    return "SO";
  } else if (x >= 146.25 && x < 168.75) {
    return "SSO";
  } else if (x >= 168.75 && x < 191.25) {
    return "S";
  } else if (x >= 191.25 && x < 213.75) {
    return "SSW";
  } else if (x >= 213.75 && x < 236.25) {
    return "SW";
  } else if (x >= 236.25 && x < 258.75) {
    return "WSW";
  } else if (x >= 258.75 && x < 281.25) {
    return "W";
  } else if (x >= 281.25 && x < 303.75) {
    return "WNW";
  } else if (x >= 303.75 && x < 326.25) {
    return "NW";
  } else if (x >= 326.25 && x < 348.75) {
    return "NNW";
  } else {
    return "UNDEFINED";
  }
}

export default function WindPlot({ vmax, speed, dir }) {
  const forecast_date = vmax.coords.valid_time.data.map((date) => new Date(date));
  const [speed_q25, speed_q50, speed_q75] = ConvertWind(speed);
  const [vmax_q25, vmax_q50, vmax_q75] = ConvertWind(vmax);
  const dir_q50 = dir.data[0];
  const id = React.useId();
  const clipPathId = `${id}-clip-path`;
  const speedFormatter = (value) => `${round(value, 1)} Knoten`;
  const dirFormatter = (value) => `${TranslateDir(value)}`;
  const midnight = forecast_date.filter((element) => element.getHours() === 0);
  const current_time = Date.now();
  const plot_center = Math.max(...vmax_q50) / 2; // find center of plot by halving the maximum vmax_q50
  // console.log(current_time);
  //   const night_start = forecast_date.filter(element => element.getHours() === 20);
  return (
    <ChartsContainer
      sx={{ minWidth: 1235, mb: -2, ml: -2 }}
      series={[
        {
          yAxisId: "leftAxis",
          type: "line",
          data: vmax_q50,
          showMark: false,
          label: "Böen",
          valueFormatter: speedFormatter,
          color: "#03a9f4",
        },
        {
          yAxisId: "leftAxis",
          type: "line",
          data: speed_q50,
          showMark: false,
          label: "Mittel",
          valueFormatter: speedFormatter,
          color: "#ffc107",
        },
        {
          yAxisId: "rightAxis",
          type: "line",
          data: dir_q50,
          showMark: false,
          label: "Richtung",
          valueFormatter: dirFormatter,
          color: "#0cac0491",
        },
      ]}
      xAxis={[
        {
          data: forecast_date,
          scaleType: "time",
          valueFormatter: (date, context) =>
            context.location === "tick"
              ? `${date.toLocaleDateString("de-DE", {
                  weekday: "short",
                  hour: "2-digit",
                })}`
              : `${date.toLocaleDateString("de-DE", {
                  weekday: "long",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                })} LT`,
          //label: "Wind",
          min: forecast_date.at(0),
          max: forecast_date.at(-1),
        },
      ]}
      yAxis={[
        { id: "leftAxis", label: "Geschwindigkeit (Knoten)", width: 50, position: "left" },
        { id: "rightAxis", label: "Richtung (°)", width: 50, position: "right" },
      ]}
      height={340}
    >
      <ChartsXAxis />
      <ChartsYAxis axisId="rightAxis" />
      <ChartsYAxis axisId="leftAxis" />
      <g clipPath={`url(#${clipPathId})`}>
        <DayLines midnight={midnight} current_time={current_time} />
        <ChartsReferenceLine
          y={14}
          lineStyle={{ strokeDasharray: "10 5", stroke: "#1080ffff" }}
          labelStyle={{ fontSize: "12", lineHeight: 1.2, fill: "#1080ffff" }}
          label={`Wingable`}
        />
        <ChartsReferenceLine
          y={round(plot_center, 0)}
          lineStyle={{ stroke: "#ffffff09" }}
          labelStyle={{ fontWeight: "bold", opacity: 0.5 }}
          label={`Datenquelle: MeteoSchweiz`}
        />
        <UncertaintyArea lower={speed_q25} upper={speed_q75} color={"#ff91001d"} />
        <UncertaintyArea lower={vmax_q25} upper={vmax_q75} color={"#35baf633"} />
        <LinePlot />
      </g>
      <ChartsGrid horizontal="true" />
      <MarkPlot />
      <LineHighlightPlot />
      <ChartsAxisHighlight x="line" />
      <ChartsTooltip />
      {/* <ChartsClipPath id={clipPathId} /> */}
    </ChartsContainer>
  );
}
